/**
 * Base URL for server-side Strapi requests inside the Docker/internal network.
 * Falls back to localhost in local development environments.
 */
const STRAPI_INTERNAL_URL = process.env.STRAPI_API_URL || "http://localhost:1337";

/**
 * Public URL accessible to browser clients and media loaders.
 * Used for client-side POST submissions and public asset URL construction.
 */
const STRAPI_PUBLIC_URL = process.env.NEXT_PUBLIC_STRAPI_MEDIA_URL || STRAPI_INTERNAL_URL;

/**
 * Standard envelope returned by Strapi v4/v5 REST API endpoints.
 */
export interface StrapiResponse<T = any> {
  /** Payload returned by Strapi, containing entity attributes or collection arrays */
  data: T;
  /** Pagination and query metadata */
  meta?: Record<string, unknown>;
}

/**
 * Fetches data from Strapi's REST API with incremental static revalidation.
 *
 * Designed for server components. Automatically constructs the full internal API URL,
 * passes default revalidation cache options (60 seconds), and logs structured Strapi error
 * details if the response is non-2xx before throwing an Error.
 *
 * @param endpoint - Strapi API route segment without `/api` prefix, e.g. `"/courses"`
 * @param query - URL query string including leading `?`, e.g. `"?populate=*"`
 * @returns Strapi response wrapper containing `data` and optional `meta`
 * @throws When the HTTP response status is not OK (2xx)
 */
export async function fetchStrapi<T = any>(
  endpoint: string, 
  query: string = "?populate=*"
): Promise<StrapiResponse<T>> {
  const url = `${STRAPI_INTERNAL_URL}/api${endpoint}${query}`;

  const res = await fetch(url, {
    next: { revalidate: 60, tags: ["strapi"] },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    console.error("STRAPI ERROR DETAILS:", JSON.stringify(errorData, null, 2));
    throw new Error(`Failed to fetch data from ${url}`);
  }

  return res.json();
}

/**
 * Normalizes a Strapi media URL into a complete, browser-loadable URL string.
 *
 * Preserves existing absolute URLs (such as Cloudinary or AWS S3 links) while
 * prepending `STRAPI_PUBLIC_URL` to relative media paths (e.g. `/uploads/...`).
 *
 * @param url - Raw image or file path string from Strapi media object
 * @returns Absolute URL string, or `undefined` if the input is falsy
 */
const MEDIA_VERSION = "3";

export function getStrapiMedia(url: string | null | undefined): string | undefined {
  if (!url) return undefined;

  const fullUrl =
    url.startsWith("http") || url.startsWith("//")
      ? url
      : `${STRAPI_PUBLIC_URL}${url}`;

  // only add the version to our own uploaded images
  if (fullUrl.includes("/uploads/")) {
    return `${fullUrl}${fullUrl.includes("?") ? "&" : "?"}v=${MEDIA_VERSION}`;
  }

  return fullUrl;
}

/**
 * Submits a JSON payload to a Strapi REST API endpoint via HTTP POST.
 *
 * Used primarily for form submissions (e.g. inquiries, lead captures) and client-side
 * interactions. Dispatches requests to `STRAPI_PUBLIC_URL` and parses Strapi error
 * structures for descriptive exception messages.
 *
 * @param endpoint - Target Strapi API route segment without `/api`, e.g. `"/leads"`
 * @param payload - Request body containing `{ data: ... }` expected by Strapi
 * @returns Parsed JSON response payload from the Strapi server
 * @throws When the HTTP response indicates failure, surfacing Strapi's error message
 */
export async function postStrapi<T = any>(
  endpoint: string,
  payload: { data: Record<string, unknown>; [key: string]: unknown }
): Promise<T> {
  const url = `${STRAPI_PUBLIC_URL}/api${endpoint}`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    console.error("STRAPI POST ERROR DETAILS:", JSON.stringify(errorData, null, 2));
    throw new Error(errorData?.error?.message || `Failed to submit data to ${url}`);
  }

  return res.json();
}